import { Component, computed, inject, output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { ApplicationUserService } from '../../services/application-user.service';

/** Shown by an app shell instead of its loading skeleton when the logged-in user could not be loaded. */
@Component({
  selector: 'rc-app-load-error',
  standalone: true,
  imports: [TranslateModule, ButtonComponent],
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding: 24px 16px;
        text-align: center;
      }
      .rc-app-load-error__icon {
        font-size: 32px;
        color: var(--colors-danger-500, #b0413e);
      }
      .rc-app-load-error__title {
        margin: 0;
        font-size: 18px;
        font-weight: 700;
      }
      .rc-app-load-error__hint,
      .rc-app-load-error__detail {
        margin: 0;
        color: var(--colors-neutral-600);
      }
      .rc-app-load-error__detail {
        font-size: 12px;
      }
    `,
  ],
  template: `
    <i class="rc-icon-alert-triangle rc-app-load-error__icon" aria-hidden="true"></i>
    <p class="rc-app-load-error__title" role="alert">{{ 'USER_LOAD_FAILED' | translate }}</p>
    <p class="rc-app-load-error__hint">{{ 'USER_LOAD_FAILED_HINT' | translate }}</p>
    @if (detail()) {
      <p class="rc-app-load-error__detail">{{ detail() }}</p>
    }
    <button kendoButton themeColor="primary" type="button" (click)="retry.emit()">{{ 'RETRY' | translate }}</button>
  `,
})
export class AppLoadErrorComponent {
  readonly #applicationUserService = inject(ApplicationUserService);

  /** The shell reloads the user its own way (e.g. with its own failure handling). */
  retry = output<void>();

  /** Short technical detail (status and url) to help tell a down API from a broken response. */
  detail = computed(() => {
    const err = this.#applicationUserService.loadError();
    if (err instanceof HttpErrorResponse) {
      return [err.status || null, err.statusText, err.url].filter(Boolean).join(' · ');
    }
    return err instanceof Error ? err.message : null;
  });
}
